import { useEffect, useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { fighters } from './content'
import { useFightStore } from './store'
import {impactFluidForCue} from './impactFluidsEngine'

type Particle={
 active:boolean
 position:THREE.Vector3
 velocity:THREE.Vector3
 rotation:THREE.Euler
 angular:THREE.Vector3
 life:number
 maxLife:number
 size:number
 color:THREE.Color
 gravity:number
 drag:number
 bounce:number
}

function pool(count:number):Particle[]{
 return Array.from({length:count},()=>({
  active:false,
  position:new THREE.Vector3(),
  velocity:new THREE.Vector3(),
  rotation:new THREE.Euler(),
  angular:new THREE.Vector3(),
  life:0,maxLife:1,size:1,
  color:new THREE.Color('#ffffff'),
  gravity:9.8,drag:1,bounce:.2,
 }))
}

const temp=new THREE.Object3D()
const white=new THREE.Color('#ffffff')
const warm=new THREE.Color('#ffd9aa')
const darkDust=new THREE.Color('#6b625b')

function seed01(serial:number,index:number,channel:number){
 let x=(serial*73856093)^(index*19349663)^(channel*83492791)
 x^=x<<13;x^=x>>>17;x^=x<<5
 return ((x>>>0)%10000)/9999
}

function activate(
 particle:Particle,
 position:THREE.Vector3,
 velocity:THREE.Vector3,
 life:number,
 size:number,
 color:THREE.Color,
 gravity:number,
 drag:number,
 bounce:number,
 angular=1,
){
 particle.active=true
 particle.position.copy(position)
 particle.velocity.copy(velocity)
 particle.rotation.set(0,0,0)
 particle.angular.set(angular*.73,angular,angular*.41)
 particle.life=life
 particle.maxLife=life
 particle.size=size
 particle.color.copy(color)
 particle.gravity=gravity
 particle.drag=drag
 particle.bounce=bounce
}

function updatePool(mesh:THREE.InstancedMesh|null,particles:Particle[],dt:number,kind:'spark'|'shard'|'dust'){
 if(!mesh)return
 for(let i=0;i<particles.length;i++){
  const p=particles[i]
  if(!p.active){
   temp.position.set(0,-50,0);temp.scale.setScalar(0);temp.rotation.set(0,0,0);temp.updateMatrix();mesh.setMatrixAt(i,temp.matrix)
   continue
  }
  p.life-=dt
  if(p.life<=0){p.active=false;i--;continue}
  const damp=Math.exp(-p.drag*dt)
  p.velocity.x*=damp;p.velocity.z*=damp
  if(kind!=='dust')p.velocity.y-=p.gravity*dt
  else p.velocity.y+=.08*dt
  p.position.addScaledVector(p.velocity,dt)
  if(p.position.y<.04&&kind!=='dust'){
   if(Math.abs(p.velocity.y)>.45&&p.bounce>0){
    p.position.y=.04;p.velocity.y=Math.abs(p.velocity.y)*p.bounce;p.velocity.x*=.68;p.velocity.z*=.68
   }else{p.position.y=.04;p.velocity.set(0,0,0)}
  }
  p.rotation.x+=p.angular.x*dt;p.rotation.y+=p.angular.y*dt;p.rotation.z+=p.angular.z*dt
  const life01=Math.max(0,p.life/p.maxLife)
  const scale=p.size*(kind==='dust'?(1+(1-life01)*1.7)*Math.min(1,life01*3):Math.min(1,life01*4))
  temp.position.copy(p.position);temp.rotation.copy(p.rotation);temp.scale.setScalar(scale);temp.updateMatrix()
  mesh.setMatrixAt(i,temp.matrix)
  const c=p.color.clone().multiplyScalar(kind==='dust'?.65+life01*.35:.72+life01*.5)
  mesh.setColorAt(i,c)
 }
 mesh.instanceMatrix.needsUpdate=true
 if(mesh.instanceColor)mesh.instanceColor.needsUpdate=true
}
export function CombatImpactParticles(){
 const cue=useFightStore(s=>s.impactCue)
 const playerIndex=useFightStore(s=>s.playerIndex),enemyIndex=useFightStore(s=>s.enemyIndex)
 const sparks=useRef(pool(42)),shards=useRef(pool(28)),dust=useRef(pool(26)),fluids=useRef(pool(24))
 const sparkCursor=useRef(0),shardCursor=useRef(0),dustCursor=useRef(0),fluidCursor=useRef(0)
 const sparkMesh=useRef<THREE.InstancedMesh>(null),shardMesh=useRef<THREE.InstancedMesh>(null),dustMesh=useRef<THREE.InstancedMesh>(null),fluidMesh=useRef<THREE.InstancedMesh>(null)
 const sourceColor=useMemo(()=>new THREE.Color(cue?.source==='enemy'?fighters[enemyIndex]?.accent??'#ff8a7a':fighters[playerIndex]?.accent??'#78dfff'),[cue?.source,playerIndex,enemyIndex])

 useEffect(()=>{
  for(const mesh of [sparkMesh.current,shardMesh.current,dustMesh.current,fluidMesh.current])if(mesh){
   mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
   for(let i=0;i<mesh.count;i++){temp.position.set(0,-50,0);temp.scale.setScalar(0);temp.updateMatrix();mesh.setMatrixAt(i,temp.matrix)}
   mesh.instanceMatrix.needsUpdate=true
  }
 },[])

 useEffect(()=>{
  if(!cue)return
  const state=useFightStore.getState()
  const attacker=cue.source==='player'?state.player:state.enemy
  const origin=new THREE.Vector3(cue.x,cue.y??1.05,cue.z)
  const dir=Math.sign(cue.x-attacker.x)||1
  const special=cue.attackKind==='special',heavy=cue.attackKind==='heavy'||cue.kind==='guard-break',blocked=cue.kind==='block'
  const energy=special?1.65:heavy?1.3:cue.attackKind==='throw'?1.15:blocked?.62:.9
  const sparkCount=Math.round((special?22:heavy?16:blocked?7:10)*energy)
  const shardCount=Math.round((special?12:heavy?9:blocked?3:5)*energy)
  const dustCount=Math.round((special?14:heavy?11:blocked?6:8)*energy)

  for(let i=0;i<sparkCount;i++){
   const j=sparkCursor.current++%sparks.current.length,p=sparks.current[j]
   const a=(seed01(cue.serial,i,1)-.5)*1.45
   const up=.35+seed01(cue.serial,i,2)*1.15
   const speed=(2.8+seed01(cue.serial,i,3)*4.8)*energy
   const vel=new THREE.Vector3(dir*Math.cos(a)*speed,up*speed*.72,Math.sin(a)*speed*.58)
   const color=seed01(cue.serial,i,4)>.36?sourceColor:white
   activate(p,origin,vel,.24+seed01(cue.serial,i,5)*.42,.025+seed01(cue.serial,i,6)*.045,color,5.8,2.4,.08,5+seed01(cue.serial,i,7)*8)
  }
  for(let i=0;i<shardCount;i++){
   const j=shardCursor.current++%shards.current.length,p=shards.current[j]
   const a=(seed01(cue.serial,i,8)-.5)*2.1
   const speed=(1.45+seed01(cue.serial,i,9)*2.8)*energy
   const vel=new THREE.Vector3(dir*Math.cos(a)*speed,.8+seed01(cue.serial,i,10)*2.8,Math.sin(a)*speed*.8)
   const color=cue.stageMaterial?sourceColor.clone().lerp(darkDust,.55):sourceColor.clone().lerp(warm,.26)
   activate(p,origin,vel,.6+seed01(cue.serial,i,11)*.75,.045+seed01(cue.serial,i,12)*.08,color,9.4,1.1,.24,2.5+seed01(cue.serial,i,13)*5)
  }
  for(let i=0;i<dustCount;i++){
   const j=dustCursor.current++%dust.current.length,p=dust.current[j]
   const a=seed01(cue.serial,i,14)*Math.PI*2,r=.18+seed01(cue.serial,i,15)*.42
   const pos=origin.clone().add(new THREE.Vector3(Math.cos(a)*r,-.78+seed01(cue.serial,i,16)*.24,Math.sin(a)*r))
   const vel=new THREE.Vector3(dir*(.25+seed01(cue.serial,i,17)*.5),.18+seed01(cue.serial,i,18)*.58,(seed01(cue.serial,i,19)-.5)*.9)
   const color=darkDust.clone().lerp(sourceColor,.16)
   activate(p,pos,vel,.75+seed01(cue.serial,i,20)*1.1,.12+seed01(cue.serial,i,21)*.18,color,0,.65,0,.3)
  }

  const attackerId=fighters[cue.source==='player'?playerIndex:enemyIndex]?.id??'unknown'
  const defenderId=fighters[cue.source==='player'?enemyIndex:playerIndex]?.id??'unknown'
  const fluid=impactFluidForCue(cue.kind,attackerId,defenderId,cue.damage??0)
  const budget=(window as typeof window&{__INYRON_RUNTIME_BUDGET__?:{capFluidDroplets:number}}).__INYRON_RUNTIME_BUDGET__
  const fluidCount=fluid?Math.min(fluids.current.length,fluid.droplets,budget?.capFluidDroplets??24):0
  if(fluid)for(let i=0;i<fluidCount;i++){
   const p=fluids.current[fluidCursor.current++%fluids.current.length]
   const a=seed01(cue.serial,i,30)*Math.PI*2,speed=(.75+seed01(cue.serial,i,31)*2.5)*fluid.impulse
   const vel=new THREE.Vector3(dir*speed+.35*Math.cos(a),1.2+seed01(cue.serial,i,32)*1.65,Math.sin(a)*speed)
   activate(p,origin,vel,fluid.lifeMs/1000,.042+seed01(cue.serial,i,33)*.048,new THREE.Color(fluid.color),8,1.1,.08,2.5)
  }
  window.dispatchEvent(new CustomEvent('inyron:particle-impact',{detail:{
   serial:cue.serial,attackKind:cue.attackKind,kind:cue.kind,
   sparks:sparkCount,shards:shardCount,dust:dustCount,fluids:fluidCount,fluidKind:fluid?.kind??null,damage:cue.damage??0,
  }}))
 },[cue?.serial,sourceColor])

 useFrame((_,dt)=>{
  const step=Math.min(.033,Math.max(0,dt))
  updatePool(sparkMesh.current,sparks.current,step,'spark')
  updatePool(shardMesh.current,shards.current,step,'shard')
  updatePool(dustMesh.current,dust.current,step,'dust')
  updatePool(fluidMesh.current,fluids.current,step,'shard')
 })

 return <group name="combat-impact-particles">
  <instancedMesh ref={sparkMesh} args={[undefined,undefined,sparks.current.length]} frustumCulled={false}>
   <boxGeometry args={[.035,.018,.18]}/>
   <meshBasicMaterial transparent opacity={.92} vertexColors toneMapped={false}/>
  </instancedMesh>
  <instancedMesh ref={shardMesh} args={[undefined,undefined,shards.current.length]} frustumCulled={false}>
   <tetrahedronGeometry args={[.12,0]}/>
   <meshStandardMaterial roughness={.48} metalness={.22} vertexColors/>
  </instancedMesh>
  <instancedMesh ref={dustMesh} args={[undefined,undefined,dust.current.length]} frustumCulled={false}>
   <sphereGeometry args={[.32,7,5]}/>
   <meshBasicMaterial transparent opacity={.16} depthWrite={false} vertexColors/>
  </instancedMesh>
  <instancedMesh ref={fluidMesh} args={[undefined,undefined,fluids.current.length]} frustumCulled={false}>
   <sphereGeometry args={[.075,6,6]}/>
   <meshStandardMaterial roughness={.32} metalness={.06} vertexColors/>
  </instancedMesh>
 </group>
}
